import { findRequiredFields } from "@/common/utils/validationHelper";
import * as schema from "yup";
import { SiteAreaFormModel } from "../models/siteArea";
import { AddressFormModel } from "@/common/models/model";

export const siteAreaTariffLength = {
  maxLength: 36,
};

export const formSchema = schema.object<
  Partial<Record<keyof SiteAreaFormModel, schema.AnySchema>>
>({
  name: schema.string().trim().required("general.mandatory_field"),
  site: schema.string().trim().required("general.mandatory_field"),
  subStationID: schema.string().trim().required("general.mandatory_field"),
  feederID: schema.string().trim().required("general.mandatory_field"),
  transformerID: schema.string().trim().required("general.mandatory_field"),
  address: schema.object<
    Partial<Record<keyof AddressFormModel, schema.AnySchema>>
  >({
    coordinates: schema.array().of(
      schema
        .string()
        .trim()
        .matches(/^-?\d*(\.\d+)?$|^$/, "general.invalid_value")
        .test({
          name: "coordinatesRange",
          message: "general.mandatory_field",
          test: (value, context) => {
            if (!value || isNaN(+value)) {
              return true;
            }
            if (+context.parent[0] == +value) {
              const longitude = +context.parent[0];
              return longitude >= -180 && longitude <= 180;
            } else if (+context.parent[1] == +value) {
              const latitude = +context.parent[1];
              return latitude >= -90 && latitude <= 90;
            } else {
              return true;
            }
          },
        })
    ),
  }),
  tariffID: schema
    .string()
    .trim()
    .max(siteAreaTariffLength.maxLength, "general.error_max_length")
    .min(0)
    .nullable(true),
  maximumPower: schema
    .string()
    .trim()
    .required("general.mandatory_field")
    .test({
      name: "nonNegative",
      message: "Power cannot be negative",
      test: (value) => {
        if (!value) return true;
        const numValue = parseFloat(value);
        return !isNaN(numValue) && numValue >= 0;
      },
    }),
  configuredPower: schema
    .string()
    .trim()
    .required("general.mandatory_field")
    .test({
      name: "nonNegative",
      message: "Configured power cannot be negative",
      test: (value) => {
        if (!value) return true;
        const numValue = parseFloat(value);
        return !isNaN(numValue) && numValue >= 0;
      },
    }),
  ampPerPhase: schema
    .string()
    .required("general.mandatory_field")
    .test({
      name: "greaterThanZero",
      message: "Breaker Size must be greater than 0 Amps",
      test: (value) => {
        if (!value) return true;
        const numValue = parseFloat(value);
        return !isNaN(numValue) && numValue > 0;
      },
    })
    .test({
      name: "maxLimit",
      message: "Breaker Size cannot exceed 10000 Amps",
      test: (value) => {
        if (!value) return true;
        const numValue = parseFloat(value);
        return !isNaN(numValue) && numValue <= 10000;
      },
    }),
  voltage: schema.string().trim().required("general.mandatory_field"),
  totalAmpAllPhases: schema.string(),
  smartCharging: schema
    .object()
    .shape({
      enabled: schema.boolean(),
      smartChargingtype: schema.string().nullable(),
      // setPointAmp: schema.number().when("smartChargingtype", {
      //   is: "loadBalancing",
      //   then: (schema) =>
      //     schema.required("general.mandatory_field").test({
      //       name: "validLoadBalancingSetpoint",
      //       message: "Setpoint Per Phase must be greater than 0",
      //       test: (value: any) => value > 0,
      //     }),
      //   otherwise: (schema) => schema.nullable(),
      // }),
      // setPointkw: schema.number().when("smartChargingtype", {
      //   is: "demandManagement",
      //   then: (schema) =>
      //     schema.required("general.mandatory_field").test({
      //       name: "validDemandSetpoint",
      //       message: "Demand setpoint must be greater than 0",
      //       test: (value: any) => value > 0,
      //     }),
      //   otherwise: (schema) => schema.nullable(),
      // }),
    })
    .test({
      name: "demandSetpointLessThanPower",
      message: "Demand setpoint must be less than total power",
      test: function (value) {
        // Only apply this validation for demand management
        if (value?.smartChargingtype === "demandManagement") {
          const setPointkw = Number(value.setPointkw);
          const maximumPower = this.parent.maximumPower;

          // Skip validation if either value is missing
          if (!setPointkw || !maximumPower) return true;

          // Convert maximumPower to number if it's a string
          const maximumPowerNum =
            typeof maximumPower === "string"
              ? parseFloat(maximumPower)
              : maximumPower;

          // The validation rule: setPointkw must be less than maximumPower
          return setPointkw <= maximumPowerNum;
        }
        return true;
      },
    })
    .test({
      name: "setpointAmpLessThanRatedAmpPerPhase",
      message: "Setpoint Amp must be less than Rated limit (amp per phase)",
      test: function (value) {
        // Only apply this validation for load balancing
        if (value?.smartChargingtype === "loadBalancing") {
          const setPointAmp = Number(value.setPointAmp);
          const ratedAmpPerPhase = this.parent.ratedAmpPerPhase;

          // Skip validation if either value is missing
          if (!setPointAmp || !ratedAmpPerPhase) return true;

          // Convert ratedAmpPerPhase to number if it's a string
          const ratedAmpPerPhaseNum =
            typeof ratedAmpPerPhase === "string"
              ? parseFloat(ratedAmpPerPhase)
              : ratedAmpPerPhase;

          // The validation rule: setPointAmp must be less than ratedAmpPerPhase
          return setPointAmp <= ratedAmpPerPhaseNum;
        }
        return true;
      },
    }),
  ratedAmpPerPhase: schema
    .string()
    .trim()
    .required("general.mandatory_field")
    .test({
      name: "nonNegative",
      message: "Rated limit (amp per phase) cannot be negative",
      test: (value) => {
        if (!value) return true;
        const numValue = parseFloat(value);
        return !isNaN(numValue) && numValue >= 0;
      },
    }),
  unmanagedLoadSource: schema.string().oneOf(["manual", "submeter"]).optional(),
  subMeterTelemetryUrl: schema.string().optional(),
  installedChargers: schema.number().optional(),
  totalInstallAmp: schema.number().optional(),
  totalInstallKw: schema.number().optional(),
  totalAmpA: schema.number().optional(),
  totalAmpB: schema.number().optional(),
  totalAmpC: schema.number().optional(),
  unmanagedLoads: schema.array().of(
    schema.object({
      id: schema.string().optional(),
      loadName: schema.string().optional(),
      numberOfPhases: schema.number().optional(),
      phaseAssignment: schema.string().optional(),
      loadSizeAmp: schema.number().optional(),
      loadPowerKw: schema.number().optional(),
    })
  ).optional(),
});

export const requiredFields = findRequiredFields(
  "",
  formSchema.describe().fields
);
